/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { type Interpolation, css } from '@emotion/react'
import type {
  ONBOARDING_PAGE_NAMES,
  PAGE_NAMES,
  RENTER_NUDGE_PAGE_NAMES,
  ST_ONBOARDING_PAGE_NAMES,
} from '@zcode/mixpanel'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import type {
  Listable,
  ListableImage,
  Pin,
  Property,
  RawPinInventorySearch,
} from '@zcode/state-type'
import cn from 'classnames'
import styles from './ListingCardImageSection.module.scss'
import type { ListingCardStyle } from './ListingCardStyles'
import { ListingCardSizes } from './constants'
import { useListingCardImageStyles } from './useListingCardImageStyles'

export interface CarouselWrapperProps {
  borderRadius?: string
  customImageHeight?: number
  customImageWidth?: number
  entity: Listable | Pin<RawPinInventorySearch>
  forceIntersected?: boolean
  hasImage: boolean
  shouldPreloadImage?: boolean
  images: readonly ListableImage[] | null
  groupId?: number
}

export type PageName =
  | (typeof PAGE_NAMES)[keyof typeof PAGE_NAMES]
  | (typeof ONBOARDING_PAGE_NAMES)[keyof typeof ONBOARDING_PAGE_NAMES]
  | (typeof RENTER_NUDGE_PAGE_NAMES)[keyof typeof RENTER_NUDGE_PAGE_NAMES]
  | (typeof ST_ONBOARDING_PAGE_NAMES)[keyof typeof ST_ONBOARDING_PAGE_NAMES]

export interface FavoriteProps {
  borderStyle?: 'none' | 'solid'
  className?: string
  disabled?: boolean
  fill?: string
  fillFavorite?: string
  fillOpacity?: number
  fillOpacityFavorite?: number
  isFavorite?: boolean
  isMobile?: boolean
  isRoundButton?: boolean
  onToggle?: (isFavorite: boolean) => void
  shouldUseTransparentBg?: boolean
  size?: number
  stroke?: string
  strokeFavorite?: string
  strokeHover?: string
  analyticChannel?:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.GALLERY
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.RELATED_MULTI_MESSAGE
    | typeof Origin.FAVORITES.BUILDING
    | typeof Origin.FAVORITES.LISTING
  analyticsOrigin?: string
  disableTracking?: boolean
  entity: Listable | Property | Pin<RawPinInventorySearch> | null | undefined
  onUnfavorite?: (entity: Listable | Property | Pin<RawPinInventorySearch>) => void
  pageName?: PageName
  parentName?: string
}

export interface ListCardBadgesProps {
  entity: Listable | Pin<RawPinInventorySearch>
}

export interface ListingCardImageSectionProps {
  entity: Listable | Pin<RawPinInventorySearch>
  forceIntersected?: boolean
  forceDesktop?: boolean
  forceMobile?: boolean
  imageContainerProps?: ImageContainerProps
  now: number
  parentName: FavoriteProps['parentName']
  shouldPreloadImage?: boolean
  showBorder: boolean
  showCheckbox: boolean
  showPropertyBadge: boolean
  size: ListingCardSizes
  Carousel: React.FunctionComponent<CarouselWrapperProps>
  FavoriteContainer: React.ComponentType<FavoriteProps>
  ListCardBadges: React.ComponentType<ListCardBadgesProps>
}

export type ImageContainerProps = {
  readonly borderRadius: string
}

const listingCardImageContainer = (
  containerBorderRadius: ListingCardStyle,
  containerHeight: ListingCardStyle,
  containerMaxHeight: ListingCardStyle,
  containerMinHeight: ListingCardStyle,
  imageContainerProps?: ImageContainerProps
) => css`
    border-radius: ${
      imageContainerProps?.borderRadius
        ? imageContainerProps?.borderRadius
        : typeof containerBorderRadius === 'string'
          ? containerBorderRadius
          : containerBorderRadius.base
    };
    height: ${typeof containerHeight === 'string' ? containerHeight : containerHeight.base};
    max-height: ${
      typeof containerMaxHeight === 'string' ? containerMaxHeight : containerMaxHeight.base
    };
    min-height: ${
      typeof containerMinHeight === 'string' ? containerMinHeight : containerMinHeight.base
    };
    overflow: hidden;
    position: relative;
    width: 100%;

    @media (min-width: 768px) {
        border-radius: ${
          imageContainerProps?.borderRadius
            ? imageContainerProps?.borderRadius
            : typeof containerBorderRadius === 'string'
              ? containerBorderRadius
              : containerBorderRadius.desktop
        };
        height: ${typeof containerHeight === 'string' ? containerHeight : containerHeight.desktop};
        max-height: ${
          typeof containerMaxHeight === 'string' ? containerMaxHeight : containerMaxHeight.desktop
        };
        min-height: ${
          typeof containerMinHeight === 'string' ? containerMinHeight : containerMinHeight.desktop
        };
    }

    > img {
        width: 100%;
        cursor: pointer;
        object-fit: cover;
    }
`

export const ListingCardImageSection = ({
  entity,
  forceDesktop,
  forceIntersected,
  forceMobile,
  imageContainerProps,
  now,
  parentName,
  shouldPreloadImage,
  showBorder,
  showCheckbox,
  showPropertyBadge,
  size,
  FavoriteContainer,
  ListCardBadges,
  Carousel,
}: ListingCardImageSectionProps) => {
  const formattedAgeText = `${entity.getAgeTextMini(now)} ago`
  const { containerBorderRadius, containerHeight, containerMaxHeight, containerMinHeight } =
    useListingCardImageStyles(size, forceDesktop, forceMobile, showBorder)

  return (
    <div
      className="ListingCardImageSection-container-box"
      css={listingCardImageContainer(
        containerBorderRadius,
        containerHeight,
        containerMaxHeight,
        containerMinHeight,
        imageContainerProps
      )}
    >
      <Carousel
        customImageHeight={size === ListingCardSizes.LG ? 208 : 160}
        entity={entity}
        forceIntersected={forceIntersected}
        groupId={entity.groupId}
        hasImage={entity.hasImage}
        images={entity.images}
        shouldPreloadImage={shouldPreloadImage}
      />
      {showPropertyBadge ? (
        <div
          className={cn(
            styles.propertyBadgeContainer,
            showCheckbox && styles.propertyBadgeContainerCheckbox
          )}
        >
          <ListCardBadges entity={entity} />
        </div>
      ) : null}
      <div className={styles.favoriteContainer}>
        <FavoriteContainer
          borderStyle="none"
          entity={entity}
          fill="#AAA9A7"
          fillOpacity={0.6}
          parentName={parentName}
          shouldUseTransparentBg
          size={24}
          stroke="#ffffff"
          strokeFavorite="#ffffff"
          strokeHover="#ffffff"
        />
      </div>
      <div
        className={cn(
          styles.ageContainer,
          size === ListingCardSizes.LG && styles.ageContainerLargeCard
        )}
      >
        <div className={styles.ageTextContainer}>
          <p className={styles.ageText}>{formattedAgeText}</p>
        </div>
      </div>
    </div>
  )
}
